/**
 * Ponte de estado: `ScreenDocument` (modelo novo) → lista plana (modelo antigo).
 *
 * Existe por um motivo bem específico, e vale registrar porque é o que decide
 * quando esta pasta pode ser apagada.
 *
 * O `DataGridComponent` resolve mestre-detalhe lendo
 * `builderService.currentState.nodes` — a lista PLANA de nós do EDITOR, com
 * `parentId`. Fora do editor essa lista não existe, então uma grade renderizada
 * pelo motor novo encontraria zero painéis e o mestre-detalhe simplesmente não
 * funcionaria (sem erro, sem aviso — o pior tipo de falha).
 *
 * A alternativa seria prover o `BuilderScreenBetaService` de verdade. Não dá: ele
 * tem 3.132 linhas, cinco dependências no construtor e faz
 * `tabControlService.setBuilderService(this)` — a injeção circular que o plano
 * aponta na seção 3. Instanciar isso por tela renderizada arrastaria o grafo
 * antigo inteiro para dentro do motor novo.
 *
 * Então este arquivo faz o mínimo: deriva do `ScreenDocument` os DOIS membros que
 * o `data-grid` realmente usa. Nada mais.
 *
 * **Isto some na Fase 6**, quando o `data-grid` mudar de pasta e passar a receber
 * o que precisa por entrada, em vez de buscar num serviço global.
 */
import { Injectable, inject } from '@angular/core';

import { ScreenDocument } from '../model/screen-schema';
import { ScreenNode } from '../model/node-props';
import { ScreenContext } from '../render/screen-context';

/**
 * O formato de nó que o `data-grid` espera encontrar na lista.
 *
 * É um subconjunto de `BuilderNode`: só `id`, `type`, `parentId` e `props`, que
 * é tudo que os três trechos do `data-grid` leem. Declarar o subconjunto em vez
 * de importar `BuilderNode` deixa explícito o tamanho real do acoplamento.
 */
export interface FlatLegacyNode {
  readonly id: string;
  readonly type: string;
  readonly parentId?: string;
  readonly props: unknown;
}

/**
 * Achata a árvore, preenchendo `parentId`.
 *
 * A ordem é de profundidade, igual à do `buildIndex` — não importa para o
 * `data-grid`, que só faz busca, mas manter a mesma ordem evita que dois lugares
 * do sistema respondam coisas diferentes para a mesma pergunta.
 */
export function flattenDocument(document: ScreenDocument): readonly FlatLegacyNode[] {
  const flat: FlatLegacyNode[] = [];

  const visit = (node: ScreenNode, parentId: string | undefined): void => {
    flat.push({
      id: node.id,
      type: node.type,
      parentId,
      props: node.props
    });

    if (node.children !== undefined) {
      for (const child of node.children) {
        visit(child, node.id);
      }
    }
  };

  visit(document.root, undefined);

  return flat;
}

/**
 * Ids de tudo que está abaixo de um painel, em qualquer profundidade.
 *
 * Reprodução fiel de `BuilderScreenBetaService.getNodeIdsUnderPanel` (linha 89),
 * com uma diferença de desempenho: o original faz um `forEach` sobre a lista
 * inteira A CADA nível de recursão, o que é O(n × profundidade). Aqui os filhos
 * são indexados uma vez. O resultado é o mesmo conjunto.
 */
export function nodeIdsUnderPanel(
  nodes: readonly FlatLegacyNode[],
  panelId: string
): Set<string> {

  const childrenByParent = new Map<string, FlatLegacyNode[]>();

  for (const node of nodes) {
    if (node.parentId === undefined) {
      continue;
    }
    const siblings = childrenByParent.get(node.parentId);
    if (siblings === undefined) {
      childrenByParent.set(node.parentId, [node]);
    } else {
      siblings.push(node);
    }
  }

  const ids = new Set<string>();
  const pending: string[] = [panelId];

  while (pending.length > 0) {
    const currentId = pending.pop() as string;
    const children = childrenByParent.get(currentId);

    if (children === undefined) {
      continue;
    }

    for (const child of children) {
      if (ids.has(child.id)) {
        // Ciclo. Não deveria existir, mas o documento pode vir do editor de JSON.
        continue;
      }
      ids.add(child.id);
      pending.push(child.id);
    }
  }

  return ids;
}

/**
 * O objeto que o `data-grid` recebe no lugar do `BuilderScreenBetaService`.
 *
 * Deliberadamente pequeno: se um dia o `data-grid` passar a chamar um terceiro
 * membro, isto quebra na compilação e alguém decide conscientemente se estende a
 * ponte ou se é hora de fazer a Fase 6.
 */
@Injectable()
export class LegacyStateShim {

  private readonly context = inject(ScreenContext);

  /** Memoizado por identidade do documento: achatar a cada leitura seria caro. */
  private cachedNodes: readonly FlatLegacyNode[] = [];
  private cachedFor: ScreenDocument | null = null;

  get currentState(): { nodes: readonly FlatLegacyNode[] } {
    const document = this.context.document();

    if (document === null || document === undefined) {
      return { nodes: [] };
    }
    if (this.cachedFor === document) {
      return { nodes: this.cachedNodes };
    }

    this.cachedNodes = flattenDocument(document);
    this.cachedFor = document;
    return { nodes: this.cachedNodes };
  }

  getNodeIdsUnderPanel(nodes: readonly FlatLegacyNode[], panelId: string): Set<string> {
    return nodeIdsUnderPanel(nodes, panelId);
  }
}
